import Phaser from 'phaser'
import { GameDataService } from '../../services/GameDataService'
import { QuestSystem } from '@engine/QuestSystem'
import type { QuestDefinition } from '@engine/QuestSystem'
import type { AssetManifest } from '@interfaces/index'
import { TEXTURE_KEYS } from '@interfaces/index'
import { AssetRegistry } from '../AssetRegistry'
import { getUIBridge } from '@game/UIBridge'
import i18n from '../../i18n.js'
import { S } from '../../locales/strings.js'

const ELEMENT_COLORS: Record<string, number> = {
  fire: 0xe8613a,
  water: 0x3a8ae8,
  nature: 0x4aaa3a,
  elektro: 0xe8d030,
  light: 0xf0eeff,
  shadow: 0x8a50d0,
  neutral: 0x888888,
}

const ELEMENTS = ['fire', 'water', 'nature', 'elektro', 'light', 'shadow', 'neutral'] as const

export class BootScene extends Phaser.Scene {
  /** Manifest fetched during preload via XHR; available in create() for AssetRegistry.init(). */
  private _loadedManifest: AssetManifest | null = null

  constructor() {
    super({ key: 'BootScene' })
  }

  /**
   * Attempt to load per-entity tower atlases and tile sprites from public/sprites/.
   * Tower atlases are loaded for each entry declared in the manifest.
   * If the manifest has not loaded yet, tile sprites are still attempted;
   * per-entity atlases will be lazy-loaded on first use via AssetRegistry.ensureTowerLoaded().
   *
   * Phaser fires 'loaderror' for each missing file — handled in preload() to
   * silently fall back to procedural rendering.
   */
  private _tryLoadRealAssets(): void {
    // Per-entity tower atlases (loaded from manifest; lazy-loaded on first use otherwise)
    if (this._loadedManifest !== null) {
      for (const towerId of Object.keys(this._loadedManifest.towers)) {
        const key = TEXTURE_KEYS.TOWER_ATLAS(towerId)
        this.load.atlas(key, `sprites/towers/${towerId}.png`, `sprites/towers/${towerId}.json`)
      }
      // Per-entity unit atlases
      for (const unitId of Object.keys(this._loadedManifest.units)) {
        const key = TEXTURE_KEYS.UNIT_ATLAS(unitId)
        this.load.atlas(key, `sprites/units/${unitId}.png`, `sprites/units/${unitId}.json`)
      }
    }

    // Tile sprites — only attempted when the files actually exist under public/sprites/tiles/.
    // Until real art ships, procedural textures (generated in preload) are used instead.
    // Uncomment these lines and add the PNG files to public/sprites/tiles/ when art is ready:
    // this.load.image(TEXTURE_KEYS.TILE_GRASS,    'sprites/tiles/tile_grass.png')
    // this.load.image(TEXTURE_KEYS.TILE_STONE,    'sprites/tiles/tile_stone.png')
    // this.load.image(TEXTURE_KEYS.TILE_OBSTACLE, 'sprites/tiles/tile_obstacle.png')
    // this.load.image(TEXTURE_KEYS.BASE_PLAYER,   'sprites/tiles/base_player.png')
    // this.load.image(TEXTURE_KEYS.BASE_OPPONENT, 'sprites/tiles/base_opponent.png')
    // this.load.image(TEXTURE_KEYS.SPAWN_MARKER,  'sprites/tiles/spawn_marker.png')
  }

  preload(): void {
    // ── Silently ignore missing optional sprite assets ────────────────────────
    // Phaser does NOT automatically suppress 404 errors for missing files.
    // We catch them here and fall back to the procedural textures generated below.
    this.load.on('loaderror', (file: Phaser.Loader.File) => {
      console.warn(
        `[BootScene] Asset not found (using procedural fallback): ${file.key} (${file.src as string})`
      )
      // The failed texture key will simply not exist in this.textures;
      // AssetRegistry.hasTowerAtlas() / hasTileSprite() will return false,
      // and renderers will use the procedural fallback path automatically.
    })

    // ── Load manifest synchronously via XHR so it is available before _tryLoadRealAssets ──
    // fetch() would be async and not available in Phaser preload context.
    // We use a synchronous XHR here so that _loadedManifest is ready when
    // _tryLoadRealAssets() reads it below.
    try {
      const xhr = new XMLHttpRequest()
      // Cache-busting query param ensures the browser never serves a stale manifest
      // after new sprite atlases are added to public/sprites/towers/.
      xhr.open('GET', `sprites/manifest.json?v=${Date.now()}`, false) // synchronous
      xhr.send(null)
      if (xhr.status === 200) {
        this._loadedManifest = JSON.parse(xhr.responseText) as AssetManifest
        const towerCount = Object.keys(this._loadedManifest.towers).length
        const unitCount  = Object.keys(this._loadedManifest.units).length
        console.log(`[BootScene] Manifest loaded — ${towerCount} tower atlas(es), ${unitCount} unit atlas(es):`,
          Object.keys(this._loadedManifest.towers))
      } else {
        console.warn('[BootScene] manifest.json not found — per-entity atlases will lazy-load on first use')
      }
    } catch {
      console.warn('[BootScene] Failed to load manifest.json — per-entity atlases will lazy-load on first use')
    }

    // ── Attempt to load real sprites first ───────────────────────────────────
    this._tryLoadRealAssets()

    // ── Loading bar ──────────────────────────────────────
    const barBg = this.add.graphics()
    barBg.fillStyle(0x222222)
    barBg.fillRect(
      this.scale.width / 2 - 200,
      this.scale.height / 2 - 15,
      400,
      30
    )

    const barFill = this.add.graphics()
    // Push progress to React loading screen so both the Phaser canvas bar
    // and the React overlay bar stay in sync.
    getUIBridge().setLoading(0, i18n.t(S.BOOT_LOADING_GAMEDATA))
    this.load.on('progress', (value: number) => {
      barFill.clear()
      barFill.fillStyle(0x00ff88)
      barFill.fillRect(
        this.scale.width / 2 - 200,
        this.scale.height / 2 - 15,
        400 * value,
        30
      )
      getUIBridge().setLoading(Math.round(value * 90), i18n.t(S.BOOT_LOADING_GAMEDATA))
    })

    // JSON data is loaded via Vite ES module imports in GameDataService.load(),
    // which App.tsx calls before Phaser starts. No HTTP fetch needed here.

    // ── Procedural tower textures (40×40) ────────────────
    for (const el of ELEMENTS) {
      const color = ELEMENT_COLORS[el] ?? 0x888888
      const towerGfx = this.make.graphics({ x: 0, y: 0 })
      towerGfx.fillStyle(color)
      towerGfx.fillRect(4, 4, 32, 32)
      towerGfx.lineStyle(2, 0xffffff, 0.6)
      towerGfx.strokeRect(4, 4, 32, 32)
      towerGfx.generateTexture(`tower_${el}`, 40, 40)
      towerGfx.destroy()
    }

    // ── Procedural unit textures (20×20) ─────────────────
    for (const el of ELEMENTS) {
      const color = ELEMENT_COLORS[el] ?? 0x888888
      const unitGfx = this.make.graphics({ x: 0, y: 0 })
      unitGfx.fillStyle(color)
      unitGfx.fillCircle(10, 10, 8)
      unitGfx.lineStyle(2, 0xffffff, 0.8)
      unitGfx.strokeCircle(10, 10, 8)
      unitGfx.generateTexture(`unit_${el}`, 20, 20)
      unitGfx.destroy()
    }

    // ── Cell textures (52×26 diamond) ────────────────────
    const cellEmpty = this.make.graphics({ x: 0, y: 0 })
    cellEmpty.lineStyle(1, 0x334455, 1.0)
    cellEmpty.beginPath()
    cellEmpty.moveTo(26, 0)
    cellEmpty.lineTo(52, 13)
    cellEmpty.lineTo(26, 26)
    cellEmpty.lineTo(0, 13)
    cellEmpty.closePath()
    cellEmpty.strokePath()
    cellEmpty.generateTexture('cell_empty', 52, 26)
    cellEmpty.destroy()

    const cellHighlight = this.make.graphics({ x: 0, y: 0 })
    cellHighlight.fillStyle(0xffff00, 0.5)
    cellHighlight.beginPath()
    cellHighlight.moveTo(26, 0)
    cellHighlight.lineTo(52, 13)
    cellHighlight.lineTo(26, 26)
    cellHighlight.lineTo(0, 13)
    cellHighlight.closePath()
    cellHighlight.fillPath()
    cellHighlight.generateTexture('cell_highlight', 52, 26)
    cellHighlight.destroy()

    const cellBlocked = this.make.graphics({ x: 0, y: 0 })
    cellBlocked.fillStyle(0x8b4513, 0.8)
    cellBlocked.beginPath()
    cellBlocked.moveTo(26, 0)
    cellBlocked.lineTo(52, 13)
    cellBlocked.lineTo(26, 26)
    cellBlocked.lineTo(0, 13)
    cellBlocked.closePath()
    cellBlocked.fillPath()
    cellBlocked.generateTexture('cell_blocked', 52, 26)
    cellBlocked.destroy()

    // ── Base / spawn textures ────────────────────────────
    const baseFriendly = this.make.graphics({ x: 0, y: 0 })
    baseFriendly.fillStyle(0x00ff44, 0.9)
    baseFriendly.fillRect(4, 4, 44, 44)
    baseFriendly.generateTexture('base_friendly', 52, 52)
    baseFriendly.destroy()

    const baseEnemy = this.make.graphics({ x: 0, y: 0 })
    baseEnemy.fillStyle(0xff4444, 0.9)
    baseEnemy.fillRect(4, 4, 44, 44)
    baseEnemy.generateTexture('base_enemy', 52, 52)
    baseEnemy.destroy()

    const spawnPoint = this.make.graphics({ x: 0, y: 0 })
    spawnPoint.fillStyle(0xffaa00, 0.9)
    spawnPoint.fillCircle(20, 20, 16)
    spawnPoint.generateTexture('spawn_point', 40, 40)
    spawnPoint.destroy()
  }

  create(): void {
    // Initialise AssetRegistry — pass loaded manifest so it's immediately available
    // for lazy-load decisions in TowerRenderer/UnitRenderer without an extra fetch.
    AssetRegistry.init(this, this._loadedManifest ?? undefined)

    // GameDataService was already loaded by App.tsx via Vite ES module imports.
    // Calling load() here is a safe no-op if already done (guarded by _loaded flag).
    GameDataService.getInstance().load()

    // Build quest definitions — IDs MUST match QUEST_DEFINITIONS in src/data/questDefinitions.ts
    // so that ProgressionScreen can look up live progress from the store by questId.
    const dailyQuestDefs: QuestDefinition[] = [
      {
        id: 'build_20_towers',
        title: i18n.t(S.QUEST_BUILD_20_TOWERS),
        description: i18n.t(S.QUEST_BUILD_20_TOWERS_DESC),
        type: 'daily',
        target: 20,
        metric: 'towers_placed',
        trackEvent: 'TOWER_PLACED',
        reward: { xp: 60, gold: 120, packMeterIncrease: 10 },
        expiresAt: Date.now() + 86_400_000,
      },
      {
        id: 'send_units_10',
        title: i18n.t(S.QUEST_SEND_UNITS_10),
        description: i18n.t(S.QUEST_SEND_UNITS_10_DESC),
        type: 'daily',
        target: 10,
        metric: 'units_sent',
        trackEvent: 'UNIT_SENT',
        reward: { xp: 50, gold: 100, packMeterIncrease: 10 },
        expiresAt: Date.now() + 86_400_000,
      },
      {
        id: 'win_match',
        title: i18n.t(S.QUEST_WIN_MATCH),
        description: i18n.t(S.QUEST_WIN_MATCH_DESC),
        type: 'achievement',
        target: 1,
        metric: 'matches_won',
        trackEvent: 'MATCH_END',
        reward: { xp: 150, gold: 200, packMeterIncrease: 20 },
      },
      {
        id: 'win_5_pvp',
        title: i18n.t(S.QUEST_WIN_5_PVP),
        description: i18n.t(S.QUEST_WIN_5_PVP_DESC),
        type: 'weekly',
        target: 5,
        metric: 'matches_won',
        trackEvent: 'MATCH_END',
        reward: { xp: 300, gold: 500, packMeterIncrease: 30 },
        expiresAt: Date.now() + 7 * 86_400_000,
      },
    ]
    const questSystem = new QuestSystem(dailyQuestDefs)

    // Loading is done — fade out the React overlay before starting MatchScene.
    // 90-100% covers texture generation that happens after load.on('complete').
    getUIBridge().setLoading(100, i18n.t(S.BOOT_LOADING_READY))
    // Short delay so the React bar visibly reaches 100% before hiding.
    this.time.delayedCall(400, () => {
      getUIBridge().setLoadingDone()
    })

    // Hand off the validated service and quest system to MatchScene
    this.scene.start('MatchScene', {
      gameData: GameDataService.getInstance(),
      questDefinitions: dailyQuestDefs,
      questSystem,
    })
  }
}
